import { DataBundle, Asset, EdgeRow } from '../types';
import { endpoints, url } from '../../../service/config';
import { getAuthorazieHeader, getCookie } from '../../../service/service';

export interface DataSource {
  load(filters?: any): Promise<DataBundle>;
  loadFilters(): Promise<{ domains: string[]; sectors: string[]; programs: string[]; nodeTypes: string[]; availableFlows?: string[] }>;
}

// --- Normalization helpers --------------------------------------------------

function normalizeAssetType(raw: string): string {
  const v = (raw || '').toLowerCase();

  if (v.includes('pipeline')) return 'pipeline';
  if (v.includes('dataset')) return 'dataset';
  if (v.includes('notebook')) return 'notebook';
  if (v.includes('dq')) return 'dq';
  if (v.includes('recon')) return 'recon';
  if (v.includes('databricksjob') || v.includes('job')) return 'pipeline';

  return 'other';
}

function normalizeAssets(rawAssets: any[]): Asset[] {
  return rawAssets.map((row: any) => {
    const assetTypeSource = row.AssetType ?? row.Type ?? row.Subtype ?? row.assetType ?? '';
    const name = row.Name ?? row.Label ?? row.AssetName ?? row.AssetId ?? row.id ?? '';

    return {
      ...row,
      AssetType: normalizeAssetType(assetTypeSource),
      Name: name,
      Application: row.Application ?? row.Subtype ?? row.App ?? '',
    } as Asset;
  });
}

function normalizeEdges(rawEdges: any[]): EdgeRow[] {
  return rawEdges
    .map((row: any) => {
      const From = row.From ?? row.from ?? row.Source ?? row.source ?? row.Src ?? row.src ?? '';
      const To = row.To ?? row.to ?? row.Target ?? row.target ?? row.Dst ?? row.dst ?? '';

      const Relation = row.Relation ?? row.relation ?? row.Type ?? '';
      const Context = row.Context ?? row.context ?? '';

      return { From, To, Relation, Context } as EdgeRow;
    })
    .filter((r) => r.From && r.To);
}

// --- API-based data source --------------------------------------------------

export class ApiDataSource implements DataSource {
  async load(filters?: any): Promise<DataBundle> {
    const headers = getAuthorazieHeader();
    const idToken = getCookie('id_token');
    const fetchHeaders: Record<string, string> = {
      Authorization: headers.authorization,
      'Content-Type': 'application/json;charset=UTF-8',
    };
    if (idToken) {
      fetchHeaders['id_token'] = idToken;
    }
    
    let queryUrl = `${url}${endpoints.crossflowData}`;
    let method = 'GET';
    let body: string | undefined = undefined;

    if (filters) {
      method = 'POST';
      const payload: any = {};
      if (filters.domains && filters.domains.length > 0) {
        payload.domains = filters.domains.map((d: any) => d.value ?? d);
      }
      if (filters.sectors && filters.sectors.length > 0) {
        payload.sectors = filters.sectors.map((d: any) => d.value ?? d);
      }
      if (filters.programs && filters.programs.length > 0) {
        payload.programs = filters.programs.map((d: any) => d.value ?? d);
      }
      if (filters.availableFlows && filters.availableFlows.length > 0) {
        payload.availableFlows = filters.availableFlows.map((d: any) => d.value ?? d);
      }
      if (filters.nodeTypes) {
        const activeNodeTypes = Object.entries(filters.nodeTypes)
          .filter(([_, checked]) => checked)
          .map(([type]) => type);
        if (activeNodeTypes.length > 0) {
          payload.nodeTypes = activeNodeTypes;
        }
      }
      if (filters.edgeLabels !== undefined) {
        payload.edgeLabels = filters.edgeLabels ? 'All' : 'None';
      }
      if (filters.additionalFilters && filters.additionalFilters.length > 0) {
        payload.additionalFilters = filters.additionalFilters;
      }
      body = JSON.stringify(payload);
    }

    const response = await fetch(queryUrl, {
      method,
      headers: fetchHeaders,
      body,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const json = await response.json();
    const data = json.data;

    // The server already normalizes data, but apply client-side normalization
    // as a safety net in case the server response format differs slightly.
    const assets = normalizeAssets(data.assets || []);
    const edges = normalizeEdges(data.edges || []);

    return {
      assets,
      edges,
      impacts: data.impacts || [],
      dqRules: data.dqRules || [],
      expectations: data.expectations || [],
      runs: data.runs || [],
      recons: data.recons || [],
    };
  }

  async loadFilters(): Promise<{ domains: string[]; sectors: string[]; programs: string[]; nodeTypes: string[]; availableFlows?: string[] }> {
    const headers = getAuthorazieHeader();
    const idToken = getCookie('id_token');
    const fetchHeaders: Record<string, string> = {
      Authorization: headers.authorization,
      'Content-Type': 'application/json;charset=UTF-8',
    };
    if (idToken) {
      fetchHeaders['id_token'] = idToken;
    }

    // Instead of using endpoints.crossflowFilters which might not be there, hardcode the suffix based on endpoints.crossflowData
    const endpointStr = endpoints.crossflowData || '/dataOps/crossflow/data';
    const filtersEndpoint = endpointStr.replace('/crossflow/data', '/crossflow/filters');
    const queryUrl = `${url}${filtersEndpoint}`;

    const response = await fetch(queryUrl, {
      method: 'GET',
      headers: fetchHeaders,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    const json = await response.json();
    return json.data;
  }
}

// This is what CrossFlowNavigatorPage.tsx imports
export function getDataSource(): DataSource {
  return new ApiDataSource();
}